import classnames from 'classnames';
import { Namespace, TOptions } from 'i18next';
import { ElementType, HTMLProps, ReactElement, ReactNode, isValidElement, useMemo } from 'react';
import { Trans, TransProps, useTranslation } from 'react-i18next';

export type TranslationProps = Omit<
  TransProps<string, Namespace, undefined, undefined, TOptions, HTMLProps<HTMLSpanElement>>,
  'i18nKey' | 'values' | 'parent'
> & {
  pre?: 'pre' | 'pre-line' | 'nowrap' | null;
  options?: TOptions | ReactNode[];
  balance?: boolean;
  as?: ElementType;
};

export const Translation = ({
  className,
  name,
  pre = 'pre-line',
  options = {},
  components: originComponents,
  as = 'span',
  balance = false,
  ...props
}: TranslationProps) => {
  const { t } = useTranslation();
  const { count, ordinal } = options as TOptions;
  const empty = import.meta.env.VITE_APP_ENV !== 'prod' && t(name!, { count, ordinal }) === name;
  const components = useMemo(() => {
    if (!options) return originComponents;
    const components = { ...(originComponents || {}) };
    Object.values(options).forEach((element) => {
      if (isValidElement(element)) {
        const elementName =
          typeof element.type === 'string'
            ? element.type
            : (element as ReactElement<unknown, any>).type.name;
        Object.assign(components, { [elementName]: element });
      }
    });
    return components;
  }, [options, originComponents]);

  return (
    <Trans
      className={classnames(className, {
        [`white-space-${pre}`]: pre,
        no_xlt: empty,
        'text-wrap-balance': balance,
      })}
      parent={as}
      t={t}
      {...props}
      components={components}
      values={options}
      i18nKey={name}
      tOptions={{ postProcess: ['reactForTrans'] }}
    />
  );
};

export const T = Translation;
